/**
 * Otel refresh-resume wiring — see booking-flow/BookingFlowResumer.tsx and the plan.
 * Own file (not flowConfig.ts) so `rerunSearch` can import `searchHotels` without a
 * circular import: flowConfig.ts imports `hotelPersistConfig` from here; this file only
 * imports the `HotelFlowState` type back.
 */
import type { ShoppingCart } from '../../api/types/shoppingCart';
import { searchHotels } from '../../api/endpoints/hotel';
import { bookingDraftKey } from '../../booking-flow/draftStorage';
import {
  cartIsPaid,
  clampStepIndex,
  compact,
  type BookingFlowPersistConfig,
  type ResumeAdapter,
} from '../../booking-flow/resume';
import type { HotelFlowState } from './flowConfig';

/** Must match `hotelFlowConfig.steps` order (asserted in resume.test.ts). Bump
 *  `hotelPersistConfig.version` on any change. Note: `reservation` (MakeReservation) runs
 *  BEFORE `payment` for hotel. */
const HOTEL_STEP_ORDER = ['search', 'allocate', 'guests', 'reservation', 'payment', 'document'] as const;
const idx = (id: string) => HOTEL_STEP_ORDER.indexOf(id as (typeof HOTEL_STEP_ORDER)[number]);

const FLOOR = idx('guests');
const PAYMENT = idx('payment');
const DOCUMENT = idx('document');

export const hotelPartialize = (s: HotelFlowState): Partial<HotelFlowState> =>
  compact({
    searchRequest: s.searchRequest,
    selectedOffer: s.selectedOffer,
    guests: s.guests?.length ? s.guests : undefined,
    contact: s.contact && Object.keys(s.contact).length ? s.contact : undefined,
    selectedCurrentAccount: s.selectedCurrentAccount,
    billingInformation: s.billingInformation,
    paymentType: s.paymentType,
    reservation: s.reservation,
    payment: s.payment,
    shoppingCartId: s.shoppingCartId,
  });

export const hotelPersistConfig: BookingFlowPersistConfig<HotelFlowState> = {
  key: bookingDraftKey('hotel'),
  version: 2,
  floorStepId: 'guests',
  searchResultStepId: 'allocate',
  partialize: hotelPartialize,
  /** Onaya gönderilmiş taslağı YAZMA — aksi hâlde `/hotel`'e her girişte resume edilip
   *  kullanıcı yeniden aynı bekleyen sepete düşer. */
  shouldPersist: (s, stepIndex) => {
    if (s.sentForApproval) return false;
    if (stepIndex > 0) return true;
    return Object.keys(hotelPartialize(s)).length > 0;
  },
};

export const hotelResumeAdapter: ResumeAdapter<HotelFlowState> = {
  getCartId: (draft) => draft.shoppingCartId,
  applyResumedCart: (cart: ShoppingCart) => ({ shoppingCart: cart, shoppingCartId: cart.id }),
  resolveResumeStepId: (cart: ShoppingCart, savedStepId: string) => {
    if (cartIsPaid(cart)) return HOTEL_STEP_ORDER[DOCUMENT];
    if (cart.isPriceChanged) return 'guests';
    const saved = idx(savedStepId);
    return HOTEL_STEP_ORDER[clampStepIndex(saved >= 0 ? saved : FLOOR, FLOOR, PAYMENT)];
  },
  rerunSearch: async (draft) => {
    if (!draft.searchRequest) return null;
    const searchResult = await searchHotels(draft.searchRequest);
    return { searchResult };
  },
};
